import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gem, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(username, password);
      toast.success("Welcome back to MoneyMoonX");
      navigate(user.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050507]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1643904736472-8b77e93ca3d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMHNreXNjcmFwZXIlMjBsdXh1cnklMjBnb2xkJTIwbmlnaHR8ZW58MHx8fHwxNzg2NTk4OTEzfDA&ixlib=rb-4.1.0&q=85" alt="wealth" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050507]/40 to-[#050507]" />
        <div className="absolute bottom-16 left-12 max-w-md">
          <h2 className="font-display text-4xl font-extrabold">Building Wealth. <span className="gold-text">Securing Futures.</span></h2>
          <p className="text-slate-300 mt-4">Access your investor dashboard and track your growing income.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl gold-btn grid place-items-center"><Gem className="w-5 h-5 text-black" /></div>
            <div className="font-display font-extrabold text-lg">Money<span className="gold-text">MoonX</span></div>
          </Link>
          <h1 className="font-display text-3xl font-bold">Welcome back</h1>
          <p className="text-slate-400 mt-2 text-sm">Sign in to your account to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-slate-400">Username</label>
              <input data-testid="login-username-input" required value={username} onChange={(e) => setUsername(e.target.value)}
                className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none transition-colors" placeholder="your username" />
            </div>
            <div>
              <label className="text-sm text-slate-400">Password</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none transition-colors" placeholder="••••••••" />
            </div>
            <button data-testid="login-submit-btn" disabled={busy} className="gold-btn w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              <LogIn className="w-5 h-5" /> {busy ? "Signing in..." : "Sign In"}
            </button>
            <div className="flex justify-between text-xs pt-1">
              <Link to="/forgot-username" data-testid="forgot-username-link" className="text-slate-400 hover:text-amber-300">Forgot username?</Link>
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-slate-400 hover:text-amber-300">Forgot password?</Link>
            </div>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            No account? <Link to="/register" className="text-amber-400 hover:text-amber-300 font-semibold">Get started</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
